import { useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useDeleteCrmMaster, type CrmMasterDetail } from "@/lib/api/crm/masters";
import { DeleteCrmMasterRequestValidation } from "@/lib/validation/crm";
import { RoutePath } from "../../routerPaths";
import { describeDeleteMasterError } from "../crmErrors";

type DeleteMasterState = {
  reason: string;
  error: string | null;
};

type DeleteMasterAction =
  | { type: "changeReason"; reason: string }
  | { type: "deleteFailed"; error: string };

const initialDeleteMasterState: DeleteMasterState = { reason: "", error: null };

function deleteMasterReducer(state: DeleteMasterState, action: DeleteMasterAction): DeleteMasterState {
  switch (action.type) {
    case "changeReason":
      return { ...state, reason: action.reason, error: null };
    case "deleteFailed":
      return { ...state, error: action.error };
  }
}

/** Developer only: soft-deletes a master record. Mount it only while open. */
export function DeleteMasterDialog({
  master,
  onClose,
}: {
  master: CrmMasterDetail;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [state, dispatch] = useReducer(deleteMasterReducer, initialDeleteMasterState);
  const deleteMutation = useDeleteCrmMaster(master.id);

  const recordCount = master.records.length;
  const isDeleting = deleteMutation.isLoading;
  const canSubmit = recordCount === 0 && state.reason.trim() !== "" && !isDeleting;

  const handleDelete = async (): Promise<void> => {
    const parsed = DeleteCrmMasterRequestValidation.safeParse({
      expected_revision: master.revision,
      reason: state.reason,
    });
    if (!parsed.success) {
      dispatch({ type: "deleteFailed", error: parsed.error.issues[0]?.message ?? "Check the form and try again." });
      return;
    }

    try {
      const result = await deleteMutation.mutateAsync(parsed.data);
      if (!result.success) {
        dispatch({ type: "deleteFailed", error: describeDeleteMasterError(result) });
        return;
      }
      toast({ title: `Deleted ${result.data.long_name}` });
      onClose();
      navigate(RoutePath.CrmList);
    } catch (error: unknown) {
      dispatch({
        type: "deleteFailed",
        error: error instanceof Error ? error.message : "The master record couldn't be deleted. Try again.",
      });
    }
  };

  return (
    <Dialog open onOpenChange={(nextOpen: boolean) => { if (!nextOpen && !isDeleting) onClose(); }}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Delete master record</DialogTitle>
          <DialogDescription>
            Deletes {master.long_name}. It disappears from the CRM, but its history is kept.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {recordCount > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="crm-delete-master-has-records">
              This master record still has {recordCount.toLocaleString()} record{recordCount === 1 ? "" : "s"}.
              Move {recordCount === 1 ? "it" : "them"} to another master record before deleting it.
            </p>
          )}

          <div className="grid gap-2">
            <Label htmlFor="crm-delete-master-reason">Reason</Label>
            <Textarea
              id="crm-delete-master-reason"
              placeholder="Why is this master record being deleted?"
              value={state.reason}
              onChange={(event) => dispatch({ type: "changeReason", reason: event.target.value })}
              disabled={isDeleting || recordCount > 0}
              data-testid="crm-delete-master-reason"
            />
          </div>

          {state.error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="crm-delete-master-error">
              {state.error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isDeleting}>Cancel</Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={!canSubmit}
            data-testid="crm-delete-master-submit"
          >
            {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            {isDeleting ? "Deleting…" : "Delete master record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
